import store from '../../store/store';
import dymoLabels from './dymoLabels';

export default class {
	initialized = false;
	availablePrinters = [];
	shippingPrinter = null;
	shippingPrinterInitialized = null;
	shippingPrinterConnected = null;
	productPrinter = null;
	productPrinterInitialized = null;
	productPrinterConnected = null;
	returnPrinter = null;
	returnPrinterInitialized = null;
	returnPrinterConnected = null;
	eanPrinter = null;
	eanPrinterInitialized = null;
	eanPrinterConnected = null;

	constructor() {
	}

	async init() {
		return new Promise((resolve, reject) => {
			store.dispatch('dymoStore/init').then(res => {
				this.connectDymo().then(async () => {
					this.shippingPrinter = store.getters['dymoStore/addressPrinter'];
					this.productPrinter = store.getters['dymoStore/productPrinter'];
					this.returnPrinter = store.getters['dymoStore/returnPrinter'];
					this.eanPrinter = store.getters['dymoStore/eanPrinter'];

					this.shippingPrinterInitialized = this.shippingPrinter !== null;
					this.productPrinterInitialized = this.productPrinter !== null;
					this.returnPrinterInitialized = this.returnPrinter !== null;
					this.eanPrinterInitialized = this.eanPrinter !== null;

					await setTimeout(() => {
						try {
							// eslint-disable-next-line no-undef
							const printerPromise = dymo.label.framework.getPrintersAsync();

							if (!printerPromise || typeof printerPromise.then !== 'function') {
								this.initialized = true;
								resolve();
								return;
							}

							printerPromise.then(printers => {
								if (this.shippingPrinterInitialized) {
									this.shippingPrinterConnected = this.isPrinterConnected(printers, this.shippingPrinter.name);
								}

								if (this.productPrinterInitialized) {
									this.productPrinterConnected = this.isPrinterConnected(printers, this.productPrinter.name);
								}

								if (this.returnPrinterInitialized) {
									this.returnPrinterConnected = this.isPrinterConnected(printers, this.returnPrinter.name);
								}

								if (this.eanPrinterInitialized) {
									this.eanPrinterConnected = this.isPrinterConnected(printers, this.eanPrinter.name);
								}

								this.initialized = true;

								resolve();
							}).catch(error => {
								this.initialized = true;
								resolve();
							});
						} catch (error) {
							this.initialized = true;
							resolve();
						}
					}, 100);
				}).catch(error => {
					reject();
				});
			}).catch(error => {
				reject();
			});
		});
	}

	isPrinterConnected(printers, printerName) {
		let connected = false;

		printers.forEach(printer => {
			if (printer.name === printerName) {
				if (printer.isConnected) {
					connected = true;
				}
			}
		});

		return connected;
	}

	connectDymo() {
		return new Promise((resolve, reject) => {
			// eslint-disable-next-line no-undef
			dymo.label.framework.init();

			// eslint-disable-next-line no-undef
			const dymoEnv = dymo.label.framework.checkEnvironment();

			if (dymoEnv.isBrowserSupported && dymoEnv.isFrameworkInstalled && dymoEnv.isWebServicePresent) {
				resolve();
			} else {
				reject();
			}
		});
	}

	shippingPrinterReady() {
		return this.initialized && this.shippingPrinterInitialized && this.shippingPrinterConnected;
	}

	productPrinterReady() {
		return this.initialized && this.productPrinterInitialized && this.productPrinterConnected;
	}

	returnPrinterReady() {
		return this.initialized && this.returnPrinterInitialized && this.returnPrinterConnected;
	}

	eanPrinterReady() {
		return this.initialized && this.eanPrinterInitialized && this.eanPrinterConnected;
	}

	printLabel(printerData, label, labelSet = '', amount = 1) {
		const labelData = this.convertLabelData(printerData.name, label, labelSet, amount, printerData.is_twin, printerData.twin_roll);

		return new Promise((resolve, reject) => {
			try {
				// eslint-disable-next-line no-undef
				dymo.label.framework.printLabel(labelData.printerName, labelData.printParamsXml, labelData.labelXml, labelData.labelSetXml);

				resolve();
			} catch (error) {
				reject();
			}
		});
	}

	// eslint-disable-next-line no-undef
	convertLabelData(printerName, label, labelSet = '', amount = 1, isTwin = false, twinRoll = dymo.label.framework.TwinTurboRoll.Auto) {
		let printParamsXml = '<LabelWriterPrintParams><Copies>' + amount + '</Copies>';

		// eslint-disable-next-line no-undef
		if (isTwin && (twinRoll === dymo.label.framework.TwinTurboRoll.Auto || twinRoll === dymo.label.framework.TwinTurboRoll.Left || twinRoll === dymo.label.framework.TwinTurboRoll.Right)) {
			printParamsXml += '<TwinTurboRoll>' + twinRoll + '</TwinTurboRoll>';
		}
		printParamsXml += '</LabelWriterPrintParams>';

		return {
			printerName,
			printParamsXml,
			labelXml: label.getLabel(),
			labelSetXml: labelSet
		};
	}

	printShippingLabel(order) {
		return new Promise((resolve, reject) => {
			if (!this.shippingPrinterReady()) {
				console.error('Geen verbinding met de adres label printer mogelijk');

				reject();

				return;
			}

			const shippingLabel = dymoLabels.createAddressLabelForOrderAddress(order.shipping);

			let bottomText = this.shippingPrinter.bottom_text;
			bottomText = bottomText.replace('%bestelnummer%', order.order.type_id);

			const pickList = this.getPickList(order);
			bottomText = bottomText.replace('%picklist%', pickList);

			const weightList = this.getWeightList(order);
			bottomText = bottomText.replace('%weightlist%', weightList);

			shippingLabel.setOrderFrom(bottomText);
			shippingLabel.setLabelType(this.shippingPrinter.label);
			shippingLabel.setPostalStamp(this.shippingPrinter.postnl_port_stamp);

			this.printLabel(this.shippingPrinter, shippingLabel).then(result => {
				resolve();
			}).catch(error => {
				reject();
			});
		});
	}

	printShippingLabels(orders) {
		return new Promise((resolve, reject) => {
			try {
				if (!this.shippingPrinterReady()) {
					console.error('Geen verbinding met de adres label printer mogelijk');

					reject();

					return;
				}

				// eslint-disable-next-line no-undef
				const shippingLabelSet = new dymo.label.framework.LabelSetBuilder();

				orders.forEach(order => {
					const orderLabel = shippingLabelSet.addRecord();

					let address = '';
					if (order.shipping.company !== null) {
						address += order.shipping.company + '\n';
					}
					address += order.shipping.full_name + '\n';

					address += order.shipping.full_address + '\n';

					address += order.shipping.zip_code + ' ' + order.shipping.city + '\n';

					if (order.shipping.country_code === 'NL') {
						address += 'Nederland' + '\n';
					} else if (order.shipping.country_code === 'BE') {
						address += 'België' + '\n';
					}

					orderLabel.setText('address', address);

					let bottomText = this.shippingPrinter.bottom_text;
					bottomText = bottomText.replace('%bestelnummer%', order.order.type_id);

					const pickList = this.getPickList(order);
					bottomText = bottomText.replace('%picklist%', pickList);

					const weightList = this.getWeightList(order);
					bottomText = bottomText.replace('%weightlist%', weightList);

					orderLabel.setText('bottomText', bottomText);
				});

				const shippingLabel = dymoLabels.shippingLabel;
				shippingLabel.setLabelType(this.shippingPrinter.label);
				shippingLabel.setPostalStamp(this.shippingPrinter.postnl_port_stamp);

				this.printLabel(this.shippingPrinter, shippingLabel, shippingLabelSet).then(result => {
					resolve();
				}).catch(error => {
					reject();
				});
			} catch (error) {
				console.log(error);
			}
		});
	}

	printBillingLabel(order) {
		return new Promise((resolve, reject) => {
			if (!this.shippingPrinterReady()) {
				console.error('Geen verbinding met de adres label printer mogelijk');

				reject();

				return;
			}

			const shippingLabel = dymoLabels.createAddressLabelForOrderAddress(order.billing);

			let bottomText = this.shippingPrinter.bottom_text;
			bottomText = bottomText.replace('%bestelnummer%', order.order.type_id);

			const pickList = this.getPickList(order);
			bottomText = bottomText.replace('%picklist%', pickList);

			const weightList = this.getWeightList(order);
			bottomText = bottomText.replace('%weightlist%', weightList);

			shippingLabel.setOrderFrom(bottomText);
			shippingLabel.setLabelType(this.shippingPrinter.label);
			shippingLabel.setPostalStamp(this.shippingPrinter.postnl_port_stamp);

			this.printLabel(this.shippingPrinter, shippingLabel).then(result => {
				resolve();
			}).catch(error => {
				reject();
			});
		});
	}

	printReturnLabel(amount = 1) {
		return new Promise((resolve, reject) => {
			if (!this.returnPrinterReady()) {
				console.error('Geen verbinding met de adres label printer mogelijk');

				reject();

				return;
			}

			const returnLabel = {...dymoLabels.returnLabel};

			returnLabel.setName(this.returnPrinter.bottom_text);
			returnLabel.setAddress(this.returnPrinter.retour_address);
			returnLabel.setLabelType(this.returnPrinter.label);

			this.printLabel(this.returnPrinter, returnLabel, '', amount).then(result => {
				resolve();
			}).catch(error => {
				reject();
			});
		});
	}

	printEanLabel(product) {
		return new Promise((resolve, reject) => {
			if (!this.eanPrinterReady()) {
				console.error('Geen verbinding met de ean label printer mogelijk');

				reject();

				return;
			}

			const eanLabel = {...dymoLabels.eanLabel};
			eanLabel.setLabelType(this.eanPrinter.label);
			eanLabel.setEan(product.ean);
			eanLabel.setName(product.title);

			this.printLabel(this.eanPrinter, eanLabel, '', product.quantity).then(result => {
				resolve();
			}).catch(error => {
				reject();
			});
		});
	}

	renderLabel(printerName, label) {
		return new Promise((resolve, reject) => {
			// eslint-disable-next-line no-undef
			const renderParamsXml = dymo.label.framework.createLabelRenderParamsXml({shadowDepth: 2});

			const labelData = {
				printerName,
				renderParamsXml,
				labelXml: label.getLabel(),
			};

			// eslint-disable-next-line no-undef
			dymo.label.framework.renderLabelAsync(labelData.labelXml, labelData.renderParamsXml, labelData.printerName).then(labelImage => {
				resolve(labelImage);
			})
		});
	}

	getPrinters() {
		return new Promise((resolve, reject) => {
			try {
				// eslint-disable-next-line no-undef
				const printers = dymo.label.framework.getPrinters();
				resolve(printers);
			} catch (error) {
				reject();
			}
		});
	}

	getPickList(order) {
		return order.items.map((item) => {
			return `${item.quantity * item.bundle_quantity}x ${item.reference}`
		}).join(", ");
	}

	getWeightList(order) {
		let orderItemsValid = order.items.every((item) => {
			return item.weight_in_gram !== null;
		});

		if (!orderItemsValid) {
			return "GR: -";
		}

		let totalWeight = order.items.reduce((sum, item) => {
			return sum + (item.quantity * item.bundle_quantity) * item.weight_in_gram;
		}, 0);

		return "GR: " + totalWeight;
	}

}
